import { type GenericMap } from "@client/types/GenericMap";
import { type Media as MediaCommon } from "@common/declarations";
import * as React from "react";
import { type Action, type AnyAction, type Reducer, type Store } from "redux";
import { type HttpApiResponse } from "./Api";
import { type Entities, type EntitiesSource } from "./AppState";
import { type ViewportAppState } from "./AppState/viewport";
import { ScreenType } from "./Enums";
import { type Availability, type MainSettings } from "./Settings";
import { type WithChildren } from "./WithChildren";

export * from "./AppState";
export * from "./Enums";
export * from "./GenericMap";
export * from "./LabelLink";
export * from "./Menu";
export * from "./Settings";
export * from "./Tag";

import { type Rect } from "@common/declarations";
import { type LayoutsAppState } from "./AppState/layouts";
import { type StyleSheetAppState } from "./AppState/styleSheet";

export { type Rect };

export interface TextIcon {
  text: string;
  icon: string;
}

export interface LabelName {
  label: string;
  name: string;
}

export interface Country {
  name: string;
  code: string;
  regions?: string[] /* code-name */;
}

export interface LabelValue<T> {
  label: string;
  value: T;
}

export interface ScreenVisibility {
  isVisible: boolean;
  screen: ScreenType;
}

export interface ElementVisibility {
  isVisible: boolean;
}

export interface WithPlace {
  place: string;
}

export interface WithEntities {
  entities: Entities;
}

export interface WithLayouts {
  layouts: LayoutsAppState;
}

export interface WithStyleSheet {
  styleSheet: StyleSheetAppState;
}

export interface WithSettings {
  settings: MainSettings;
}

export interface WithViewport {
  viewport: ViewportAppState;
}

export enum EntityType {
  Place = "Place",
  Popup = "Popup",
  Block = "Block",
  Row = "Row",
  Column = "Column",
  Container = "Container",
  Module = "Module",
  ModuleItem = "ModuleItem"
}

export interface WithIsEditMode {
  isEditMode: boolean;
}

export interface WithParentId {
  parentId: string | "main" | "popups";
}

export interface WithEntitiesSource {
  source: EntitiesSource;
}

export type EntityRef = WithId & WithEntitiesSource;

export interface WithChildrenIds {
  childrenIds: string[];
}

export interface WithEntityType {
  type: EntityType;
}

export type EntityOrientation = "horizontal" | "vertical";

export type Entity<TData = any> = WithId<string> &
  Nullable<WithParentId> &
  Nullable<WithChildrenIds> &
  WithEntityType & {
    gid?: string | null; // global component id
    isStatic?: boolean /* cannot be moved or cloned. */;
    orientation?: EntityOrientation;
    dimensionSize?: number | null;
    data?: TData;
    settings: MainSettings | null;
    status?: string;
  };

export interface WithClassName {
  className: string;
}
export type WithOptionalClassName = Partial<WithClassName>;

export interface WithOnChange<T> {
  onChange: (value: T, e?: React.ChangeEvent<HTMLElement>) => void;
}

export interface WithValue<T> {
  value: T;
}

export interface WithI18n {}

export interface WithLabel {
  label: string;
}

export interface Size {
  height: number;
  width: number;
}

export enum BackgroundType {
  Color = "color",
  Image = "image",
  Video = "video"
}

export enum BackgroundImagePosition {
  LeftTop = "left top",
  LeftCenter = "left center",
  LeftBottom = "left bottom",

  RightTop = "right top",
  RightCenter = "right center",
  RightBottom = "right bottom",

  CenterTop = "center top",
  CenterCenter = "center center",
  CenterBottom = "center bottom"
}

export enum BackgroundImageSize {
  Auto = "auto",
  Contain = "contain",
  Cover = "cover",
  Custom = "custom"
}

export enum BackgroundImageRepeat {
  No = "no-repeat",
  XY = "repeat",
  X = "repeat-x",
  Y = "repeat-y",
  Space = "space",
  Round = "round"
}

export interface LeftTop {
  left: number;
  top: number;
}

export interface Point {
  x: number;
  y: number;
}

export type StickablePosition = LeftTop & {
  isStick: boolean;
};

export enum HeadingLevel {
  H1 = "h1",
  H2 = "h2",
  H3 = "h3",
  H4 = "h4",
  H5 = "h5",
  H6 = "h6"
}

export const FieldType = {
  Audio: "audio",
  Checkbox: "checkbox",
  CheckoutItems: "checkoutItems",
  Composite: "composite",
  Conditional: "conditional",
  ConsentItems: "consentItems",
  Color: "color",
  Corners: "corners",
  DateTime: "dateTime",
  DateRange: "dateRange",
  File: "file",
  FlexAlign: "flexAlign",
  FormEndings: "formEndings",
  FunnelPathInfo: "funnelPathInfo",
  Icon: "icon",
  Image: "image",
  Highlight: "highlight",
  Hint: "hint",
  Linked: "linked",
  List: "list",
  ModuleItems: "moduleItems",
  Number: "number",
  PopupInfo: "popupInfo",
  Range: "range",
  RichEditor: "rich",
  Size: "size",
  Social: "social",
  Sides: "sides",
  StyleSheetColor: "styleSheetColor",
  StyleSheetHeadings: "styleSheetHeadings",
  StyleSheetLink: "styleSheetLink",
  StyleSheetText: "styleSheetText",
  Tags: "tags",
  Text: "text",
  QueryString: "queryString",
  Video: "video",
  WidthHeight: "widthHeight"
};

export const FieldSubType = {
  AutoComplete: "autoComplete",
  Checkbox: "checkbox",
  Email: "email",
  Dropdown: "dropdown",
  Font: "font",
  Inline: "inline",
  OnOff: "onOff",
  RadioButton: "radio",
  Money: "money",
  MultiSelect: "multiSelect",
  MultiSelectTree: "treeMultiSelect",
  Tree: "tree",
  Password: "password"
};

export type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

export type ID = string | number;
export interface WithId<ID = string> {
  id: ID;
}

export const Status = {
  None: "None",
  Failed: "Failed",
  Loading: "Loading",
  Ready: "Ready",
  Saving: "Saving"
};

export const EntityStatus = {
  Exporting: "Exporting",
  MakingComponent: "MakingComponent"
};

export interface CSSRect {
  top?: string | number;
  right?: string | number;
  bottom?: string | number;
  left?: string | number;
}

export type DynamicStore<TState, TAction extends Action = AnyAction> = Store<TState, TAction> & {
  dynamicReducers: object;
};

export type DynamicReducers = {
  modules: GenericMap<any>;
  builder: GenericMap<any>;
};

export interface ModuleReducersMiddlewareArgs {
  getCurrentReducers: () => DynamicReducers;
  injectDynamicReducers: (reducers: DynamicReducers) => void;
}

export interface GetMiddlewaresArgs<TState, TAction extends Action = AnyAction> {
  createReducer: (dynamicReducers: GenericMap<any>) => Reducer<TState, TAction>;
  getStore: () => DynamicStore<TState, TAction>;
  fetchPage?: (args: LoadSpaPageArgs & { baseUrl: string; portalId: string }) => Promise<HttpApiResponse<any>>;
}

export type i18n = GenericMap<string>;
export type I18n = i18n;

export interface Size {
  height: number;
  width: number;
}

export interface LogoOptions {
  fileSrc?: string | null;
  position: "left" | "right" | "center";
  style: React.CSSProperties;
  title: string;
}

export interface WithAvailability {
  availability: Availability;
}

export interface ModuleConfig {
  category?: string;
  context?: string;
  hostOnly?: boolean;
  label: string;
  labelSettingName?: string | GenericMap<string>;
  imageUrl?: string;
  obsolete?: boolean;

  subscription?: string;

  layout?: GenericMap<any>;
  item?: GenericMap<any>;
  // for multi-item modules, e.g. button
  items?: GenericMap<any>;
}

export interface With {}

export type FileType = "image";

export type EnhanceWidget = <P extends Partial<WithChildren>>(
  Component: React.ForwardRefExoticComponent<P> | React.ComponentType<P>
) => React.FunctionComponent<WithId<ID> & Partial<WithClassName> & P & { title?: string }>;

export interface FormLayout {
  stackFields: boolean;
}

export type ActionTarget = "page" | "popup";

export interface CreateModuleArgs {
  defaults?: GenericMap<any>;
  isStatic?: boolean;
  moduleType: string;
}

export type CreateModuleItemArgs = CreateModuleArgs & {
  label?: string;
  data?: any;
};

export type DnDDragModuleData = CreateModuleArgs;

export interface DnDDropInfo {
  id: string;
  isAfter: boolean;
}

export type PageRequest = {
  page: number;
  size: number;
  searchText?: string;
  sortBy?: string;
  order?: SortOrder;
};

export type PageResponse<T> = {
  items: T[];
  total: number;
};

export type PageHasMoreResponse<T> = {
  items: T[];
  hasMore: boolean;
};

export namespace Common {
  export type MediaSourceKey = MediaCommon.Source;

  export type Media = {
    mediaId: string;
    mediaStatusKey: string;
    mediaTypeKey: MediaCommon.Type;
    mediaSourceKey: MediaCommon.Source;
    sourceId: string;
    fileSize: number;
  };
}

export { IconType } from "@common/declarations";

export type SortOrder = "asc" | "desc";
export type SortType = "date" | "default" | "natural";

export interface SortInfo<T> {
  key: Extract<keyof T, string>;
  order?: SortOrder;
  type?: SortType;
}

export type LoadSpaPageArgs = {
  pageId?: string;
  path?: string;
};
